import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, ArrowLeft, ShieldCheck, ShieldOff, Ban, CheckCircle, Trash2, KeyRound, Copy, X } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { usersAPI, handleApiError } from '../../utils/api';

const ManageUsersPage = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();

  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingId, setPendingId] = useState(null);
  const [resetResult, setResetResult] = useState(null); // { username, tempPassword }

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data || []);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setError(`${errTitle} : ${details}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h2>
            <p className="text-gray-600 mb-6">Vous devez être administrateur pour accéder à cette page.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleToggleRole = async (user) => {
    const newRole = user.role === 'ADMIN' ? 'USER' : 'ADMIN';
    const confirmMsg = newRole === 'ADMIN'
      ? `Donner les droits administrateur à "${user.username}" ?`
      : `Retirer les droits administrateur de "${user.username}" ?`;
    if (!window.confirm(confirmMsg)) return;

    setPendingId(user.id);
    try {
      const response = await usersAPI.updateRole(user.id, newRole);
      setUsers((prev) => prev.map((u) => (u.id === user.id ? response.data : u)));
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible de modifier le rôle : ${errTitle} - ${details}`);
    } finally {
      setPendingId(null);
    }
  };

  const handleToggleEnabled = async (user) => {
    const nextEnabled = !user.enabled;
    const confirmMsg = nextEnabled
      ? `Réactiver le compte de "${user.username}" ?`
      : `Désactiver le compte de "${user.username}" ? Il ne pourra plus se connecter.`;
    if (!window.confirm(confirmMsg)) return;

    setPendingId(user.id);
    try {
      const response = await usersAPI.setEnabled(user.id, nextEnabled);
      setUsers((prev) => prev.map((u) => (u.id === user.id ? response.data : u)));
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible de mettre à jour le compte : ${errTitle} - ${details}`);
    } finally {
      setPendingId(null);
    }
  };

  const handleResetPassword = async (user) => {
    if (!window.confirm(`Réinitialiser le mot de passe de "${user.username}" ? Son mot de passe actuel cessera de fonctionner.`)) {
      return;
    }
    setPendingId(user.id);
    try {
      const response = await usersAPI.resetPassword(user.id);
      setUsers((prev) => prev.map((u) => (u.id === user.id ? response.data.user : u)));
      setResetResult({ username: user.username, tempPassword: response.data.tempPassword });
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible de réinitialiser le mot de passe : ${errTitle} - ${details}`);
    } finally {
      setPendingId(null);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Supprimer définitivement le compte de "${user.username}" ? Cette action est irréversible.`)) {
      return;
    }
    setPendingId(user.id);
    try {
      await usersAPI.delete(user.id);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible de supprimer : ${errTitle} - ${details}`);
    } finally {
      setPendingId(null);
    }
  };

  const filteredUsers = users.filter((user) => {
    const term = searchTerm.toLowerCase();
    return (
      user.username?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            className="text-gray-600 hover:text-blue-600 flex items-center gap-1 mb-4 text-sm"
          >
            <ArrowLeft size={16} />
            Retour à l'administration
          </button>

          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                  <Users className="mr-3" size={32} />
                  Gestion des Utilisateurs
                </h1>
                <p className="text-gray-600 mt-2">
                  {users.length} utilisateur{users.length > 1 ? 's' : ''} au total
                </p>
              </div>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher par nom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              Chargement des utilisateurs...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500">Aucun utilisateur trouvé</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-100 text-left text-sm text-gray-600">
                  <tr>
                    <th className="px-4 py-3">Nom</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Rôle</th>
                    <th className="px-4 py-3">Dernière connexion</th>
                    <th className="px-4 py-3">Statut</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-semibold text-gray-800">{user.username}</td>
                      <td className="px-4 py-3 text-gray-600">{user.email}</td>
                      <td className="px-4 py-3">
                        {user.role === 'ADMIN' ? (
                          <span className="bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-1 rounded-full">
                            Admin
                          </span>
                        ) : (
                          <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-1 rounded-full">
                            Utilisateur
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-sm">
                        {user.lastLoginAt
                          ? new Date(user.lastLoginAt).toLocaleString('fr-FR', {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })
                          : 'Jamais'}
                      </td>
                      <td className="px-4 py-3">
                        {user.enabled ? (
                          <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full">
                            Actif
                          </span>
                        ) : (
                          <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-1 rounded-full">
                            Désactivé
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleToggleRole(user)}
                            disabled={pendingId === user.id}
                            className={`p-2 rounded-lg text-white transition disabled:opacity-50 ${
                              user.role === 'ADMIN' ? 'bg-gray-500 hover:bg-gray-600' : 'bg-purple-600 hover:bg-purple-700'
                            }`}
                            title={user.role === 'ADMIN' ? 'Retirer les droits admin' : 'Donner les droits admin'}
                          >
                            {user.role === 'ADMIN' ? <ShieldOff size={16} /> : <ShieldCheck size={16} />}
                          </button>
                          <button
                            onClick={() => handleToggleEnabled(user)}
                            disabled={pendingId === user.id}
                            className={`p-2 rounded-lg text-white transition disabled:opacity-50 ${
                              user.enabled ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'
                            }`}
                            title={user.enabled ? 'Désactiver le compte' : 'Réactiver le compte'}
                          >
                            {user.enabled ? <Ban size={16} /> : <CheckCircle size={16} />}
                          </button>
                          <button
                            onClick={() => handleResetPassword(user)}
                            disabled={pendingId === user.id}
                            className="bg-orange-500 text-white p-2 rounded-lg hover:bg-orange-600 transition disabled:opacity-50"
                            title="Réinitialiser le mot de passe"
                          >
                            <KeyRound size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(user)}
                            disabled={pendingId === user.id}
                            className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                            title="Supprimer"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {resetResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => setResetResult(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
            <div className="flex items-center gap-2 mb-4">
              <KeyRound className="text-orange-500" size={24} />
              <h3 className="text-lg font-bold text-gray-800">Mot de passe réinitialisé</h3>
            </div>
            <p className="text-gray-600 text-sm mb-4">
              Voici le mot de passe temporaire de <strong>{resetResult.username}</strong>. Il ne sera plus jamais
              affiché : communique-le lui dès maintenant. Il devra en choisir un nouveau à sa prochaine connexion.
            </p>
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-3 mb-4">
              <code className="flex-1 font-mono text-lg tracking-wide text-gray-800 select-all">
                {resetResult.tempPassword}
              </code>
              <button
                onClick={() => navigator.clipboard?.writeText(resetResult.tempPassword)}
                className="text-gray-500 hover:text-blue-600 p-1"
                title="Copier"
              >
                <Copy size={18} />
              </button>
            </div>
            <button
              onClick={() => setResetResult(null)}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
            >
              J'ai noté le mot de passe
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsersPage;
